// SubscribeWithRBL — Renders an R₿L tab next to the existing Stripe button
// in the analyst subscribe flow. Reuses `info.subscription_price_eur` from
// the parent so we don't duplicate the price fetch.
//
// Behaviour:
//  - Hidden entirely if DexScreener price is unavailable.
//  - Disabled with "Te faltan X R₿L" when balance is short.
//  - Confirms before charging; reloads the parent on success.
import React, { useEffect, useState } from "react";
import { Loader2, Coins, AlertTriangle, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { API, safeJson } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { useRBLPrice } from "../../hooks/useRBLPrice";
import { tRbl } from "../../i18n/rbl-payments";

export const SubscribeWithRBL = ({ username, analystId, priceEur, onSubscribed }) => {
  const { user, token, openLogin } = useAuth();
  const { lang } = useLanguage();
  const price = useRBLPrice();
  const [balance, setBalance] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!user || !token) { setBalance(null); return; }
      try {
        const r = await fetch(`${API}/shadow/tokens/balance`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = await safeJson(r);
        if (alive && j) setBalance(Number(j.balance || 0));
      } catch (_e) { /* non-critical */ }
    };
    load();
    const t = setInterval(load, 15_000);
    return () => { alive = false; clearInterval(t); };
  }, [user, token]);

  if (price.loading || !price.available) {
    // Hide instead of showing an unavailable state — the parent's EUR button
    // remains fully functional and that's the contract.
    return null;
  }

  const rblMonthly = price.price_eur > 0 ? Math.ceil(priceEur / price.price_eur) : null;
  const insufficient = balance != null && rblMonthly != null && balance < rblMonthly;
  const missing = insufficient ? rblMonthly - balance : 0;

  const subscribe = async () => {
    if (busy) return;
    if (!user) { openLogin(); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/shadow/subscribe/rbl`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(analystId ? { analyst_id: analystId } : { username }),
      });
      const d = await safeJson(r);
      if (!r.ok) {
        const det = d?.detail || {};
        throw new Error(det?.error || det?.message || "rbl_sub_failed");
      }
      if (onSubscribed) onSubscribed(d);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      setError(e.message || "Error");
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-2 rounded-xl border border-amber-400/30 bg-gradient-to-br from-amber-500/[0.05] to-orange-500/[0.02] p-3"
      data-testid="rbl-sub-card"
    >
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em]">
        <span className="inline-flex items-center gap-1 text-amber-200">
          <Coins className="h-3 w-3" /> {tRbl(lang, "rbl_tab_rbl", { n: rblMonthly?.toLocaleString() || "—" })}
        </span>
        {price.is_cached && (
          <span className="text-amber-300/50 text-[9px]">cached</span>
        )}
      </div>
      <div className="mt-1 text-[10px] text-white/40">
        1 R₿L = ${price.price_usd?.toFixed(8)} · ≈ €{priceEur}/mo
      </div>

      {!confirming ? (
        <button
          onClick={() => (user ? setConfirming(true) : openLogin())}
          disabled={!!(user && (insufficient || rblMonthly == null))}
          data-testid="rbl-sub-btn"
          className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 px-3 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_18px_rgba(251,191,36,0.4)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Coins className="h-3 w-3" />
          {!user
            ? tRbl(lang, "rbl_login_first")
            : insufficient
            ? tRbl(lang, "rbl_insufficient", { n: missing.toLocaleString() })
            : tRbl(lang, "rbl_pay_btn", { n: rblMonthly?.toLocaleString() })}
        </button>
      ) : (
        <div className="mt-2 rounded-lg border border-amber-400/40 bg-black/40 p-2.5" data-testid="rbl-sub-confirm">
          <p className="text-[10px] text-white/65">
            {tRbl(lang, "rbl_confirm_sub", { rbl: rblMonthly.toLocaleString(), eur: priceEur })}
          </p>
          <p className="mt-1 text-[9.5px] text-amber-300/75">{tRbl(lang, "rbl_renewal_note")}</p>
          <div className="mt-2 flex gap-1.5">
            <button
              onClick={subscribe}
              disabled={busy}
              data-testid="rbl-sub-confirm-btn"
              className="flex-1 rounded-md bg-amber-400 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-black hover:bg-amber-300 disabled:opacity-50"
            >
              {busy ? <Loader2 className="inline h-3 w-3 animate-spin" /> : <Check className="inline h-3 w-3" />}
              {" "}
              {busy ? tRbl(lang, "rbl_processing") : tRbl(lang, "rbl_confirm_btn")}
            </button>
            <button
              onClick={() => { setConfirming(false); setError(null); }}
              className="rounded-md border border-white/15 px-2.5 py-1.5 text-[10px] uppercase tracking-wider text-white/65 hover:text-white"
            >
              {tRbl(lang, "rbl_cancel")}
            </button>
          </div>
        </div>
      )}

      {insufficient && (
        <Link
          to="/shadow/tokens"
          className="mt-1 inline-block text-[9px] font-mono uppercase tracking-wider text-amber-300/80 hover:text-amber-200"
        >
          {tRbl(lang, "rbl_get_more")}
        </Link>
      )}
      {error && (
        <div className="mt-1.5 rounded-md border border-red-400/30 bg-red-400/[0.06] px-2 py-1 text-[10px] text-red-300" data-testid="rbl-sub-error">
          <AlertTriangle className="inline h-2.5 w-2.5 mr-1" />
          {error}
        </div>
      )}
    </div>
  );
};

export default SubscribeWithRBL;
